import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import { FileDown, Loader2, Calendar as CalendarIcon, User as UserIcon } from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfYear, subMonths } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleLabels = {
  directeur: 'Directeur',
  manager: 'Manager',
  collaborateur: 'Collaborateur'
};

export default function AdminExport() {
  const { t } = useLanguage();
  const [team, setTeam] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [userId, setUserId] = useState('');

  // Default: current month
  const now = new Date();
  const [startDate, setStartDate] = useState(format(startOfMonth(now), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(now), 'yyyy-MM-dd'));
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await axios.get(`${API}/team`);
        // Sort: directors first, then managers, then collaborators, alphabetical within
        const sorted = [...(res.data || [])].sort((a, b) => {
          const order = { directeur: 0, manager: 1, collaborateur: 2 };
          const ra = order[a.role] ?? 99;
          const rb = order[b.role] ?? 99;
          if (ra !== rb) return ra - rb;
          return (a.last_name || '').localeCompare(b.last_name || '');
        });
        setTeam(sorted);
      } catch (err) {
        console.error('Error loading team:', err);
        toast.error(t('error') || 'Erreur de chargement');
      } finally {
        setLoadingTeam(false);
      }
    };
    fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (preset) => {
    const d = new Date();
    switch (preset) {
      case 'currentMonth':
        setStartDate(format(startOfMonth(d), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(d), 'yyyy-MM-dd'));
        break;
      case 'previousMonth': {
        const prev = subMonths(d, 1);
        setStartDate(format(startOfMonth(prev), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(prev), 'yyyy-MM-dd'));
        break;
      }
      case 'ytd':
        setStartDate(format(startOfYear(d), 'yyyy-MM-dd'));
        setEndDate(format(d, 'yyyy-MM-dd'));
        break;
      case 'last12Months': {
        const past = subMonths(d, 12);
        setStartDate(format(past, 'yyyy-MM-dd'));
        setEndDate(format(d, 'yyyy-MM-dd'));
        break;
      }
      default:
        break;
    }
  };

  const handleExport = async () => {
    if (!userId) {
      toast.error('Veuillez sélectionner un collaborateur');
      return;
    }
    if (!startDate || !endDate) {
      toast.error('Veuillez renseigner la période');
      return;
    }
    if (startDate > endDate) {
      toast.error('La date de début doit être antérieure à la date de fin');
      return;
    }

    setGenerating(true);
    try {
      const res = await axios.get(`${API}/admin/export/collaborator-excel`, {
        params: { user_id: userId, start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });

      // Extract filename from Content-Disposition header if possible
      let filename = 'export.xlsx';
      const cd = res.headers['content-disposition'];
      if (cd) {
        const m = cd.match(/filename="?([^";]+)"?/);
        if (m) filename = m[1];
      }

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Export Excel généré avec succès');
    } catch (err) {
      console.error('Export error:', err);
      // Try to read error message from blob if backend returned JSON
      let msg = 'Erreur lors de la génération du fichier';
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          if (j?.detail) msg = j.detail;
        } else if (err.response?.data?.detail) {
          msg = err.response.data.detail;
        }
      } catch (_) {
        /* ignore */
      }
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const selectedUser = team.find((u) => u.id === userId);

  return (
    <Card data-testid="admin-export-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <FileDown className="h-6 w-6 text-primary" />
          <div>
            <CardTitle>Export Excel d'un collaborateur</CardTitle>
            <CardDescription>
              Génère un fichier Excel formaté contenant toutes les données de planning
              (créneaux, absences, heures supp., récupération) et une synthèse avec
              totaux hebdomadaires et mensuels.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Collaborator selection */}
        <div className="space-y-2">
          <Label htmlFor="export-user-select" className="flex items-center gap-2">
            <UserIcon className="h-4 w-4" />
            Collaborateur
          </Label>
          <Select
            value={userId}
            onValueChange={setUserId}
            disabled={loadingTeam}
          >
            <SelectTrigger id="export-user-select" data-testid="export-user-select">
              <SelectValue
                placeholder={
                  loadingTeam
                    ? 'Chargement...'
                    : 'Sélectionnez un collaborateur'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {team.map((u) => (
                <SelectItem
                  key={u.id}
                  value={u.id}
                  data-testid={`export-user-option-${u.id}`}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ backgroundColor: u.color || '#64748B' }}
                    />
                    {u.first_name} {u.last_name}
                    <span className="text-muted-foreground text-xs">
                      ({roleLabels[u.role] || u.role})
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedUser && (
            <p className="text-xs text-muted-foreground">
              {selectedUser.email} · {selectedUser.weekly_contractual_hours ?? '—'}h/semaine
            </p>
          )}
        </div>

        {/* Period */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4" />
            Période
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="export-start-date" className="text-xs text-muted-foreground">Du</Label>
              <Input
                id="export-start-date"
                data-testid="export-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="export-end-date" className="text-xs text-muted-foreground">Au</Label>
              <Input
                id="export-end-date"
                data-testid="export-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyPreset('currentMonth')}
              data-testid="export-preset-current-month"
            >
              Mois en cours
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyPreset('previousMonth')}
              data-testid="export-preset-previous-month"
            >
              Mois précédent
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyPreset('ytd')}
              data-testid="export-preset-ytd"
            >
              Depuis le 1er janvier
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyPreset('last12Months')}
              data-testid="export-preset-12m"
            >
              12 derniers mois
            </Button>
          </div>
        </div>

        {/* Action */}
        <div className="flex justify-end pt-2">
          <Button
            onClick={handleExport}
            disabled={!userId || generating}
            data-testid="export-generate-button"
            className="gap-2"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Génération en cours...
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4" />
                Générer l'Excel
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
